import React, { useState, useEffect, useRef } from "react";

const PinManagement = ({
  tvId,
  pinSettings,
  onRefresh,
  onPinSettingsChange, // PIN 설정 변경 콜백
}) => {
  const [localPinSettings, setLocalPinSettings] = useState({
    enabled: pinSettings?.enabled || false,
    code: pinSettings?.code || "",
  });
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [tempCode, setTempCode] = useState("");

  const inputRef = useRef(null);

  // pinSettings가 변경될 때 로컬 상태 업데이트
  useEffect(() => {
    setLocalPinSettings({
      enabled: pinSettings?.enabled || false,
      code: pinSettings?.code || "",
    });
  }, [pinSettings]);

  useEffect(() => {
    if (showCodeInput && inputRef.current) {
      // 약간의 지연을 두고 focus 실행 (DOM 렌더링 완료 후)
      const timer = setTimeout(() => {
        if (inputRef.current) {
          try {
            inputRef.current.focus();
          } catch (error) {
            // focus 에러 무시
            // console.warn("Focus error ignored:", error);
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [showCodeInput]);

  const handlePinSettingsChange = (newSettings) => {
    setLocalPinSettings(newSettings);

    // 부모 컴포넌트에 설정 변경 알림 (API 호출 없음)
    if (onPinSettingsChange && typeof onPinSettingsChange === "function") {
      onPinSettingsChange(newSettings);
    }
  };

  const handleTogglePin = (enabled) => {
    if (enabled) {
      // PIN 활성화 시 코드 입력 모달 표시
      setShowCodeInput(true);
      setTempCode("");
    } else {
      alert(
        "PIN 인증은 현황판 보안을 위해 필수입니다.\nPIN을 비활성화할 수 없습니다."
      );
      // 상태를 다시 활성화로 되돌림 (UI 동기화)
      setLocalPinSettings((prev) => ({ ...prev, enabled: true }));
      return;
    }
  };

  const handleSavePin = () => {
    if (!tempCode.trim()) {
      alert("PIN 코드를 입력하세요.");
      return;
    }

    if (tempCode.length !== 6 || !/^\d{6}$/.test(tempCode)) {
      alert("PIN 코드는 6자리 숫자여야 합니다.");
      return;
    }

    handlePinSettingsChange({
      enabled: true, // 항상 true
      code: tempCode,
    });

    setShowCodeInput(false);
    setTempCode("");
  };

  const handleCancelPin = () => {
    setShowCodeInput(false);
    setTempCode("");
  };

  const handleEditPin = () => {
    setTempCode(localPinSettings.code);
    setShowCodeInput(true);
  };

  const handleSetPin = () => {
    setTempCode("");
    setShowCodeInput(true);
  };

  return (
    <div
      style={{
        background: "#f8f9fa",
        border: "2px dashed #dee2e6",
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "20px",
      }}
    >
      {/* 섹션 헤더 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "20px",
          paddingBottom: "10px",
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <span style={{ fontSize: "20px" }}>🔐</span>
        <h3
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#2d3436",
          }}
        >
          PIN 인증 관리
        </h3>
      </div>

      {/* PIN 설정 */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "15px",
          }}
        >
          <h4
            style={{
              margin: "0",
              fontSize: "16px",
              fontWeight: "600",
              color: "#2d3436",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>🔑</span>
            PIN 인증
          </h4>

          <label
            style={{
              position: "relative",
              display: "inline-block",
              width: "50px",
              height: "28px",
              marginLeft: "15px",
            }}
          >
            <input
              type="checkbox"
              checked={localPinSettings.enabled}
              onChange={(e) => handleTogglePin(e.target.checked)}
              style={{
                opacity: 0,
                width: 0,
                height: 0,
              }}
            />
            <span
              style={{
                position: "absolute",
                cursor: "pointer",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: localPinSettings.enabled ? "#00b894" : "#ccc",
                transition: "0.3s",
                borderRadius: "28px",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  content: '""',
                  height: "22px",
                  width: "22px",
                  left: localPinSettings.enabled ? "25px" : "3px",
                  bottom: "3px",
                  backgroundColor: "white",
                  transition: "0.3s",
                  borderRadius: "50%",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              />
            </span>
          </label>
        </div>

        {localPinSettings.enabled && localPinSettings.code ? (
          <div
            style={{
              background: "#e3f2fd",
              border: "1px solid #90caf9",
              borderRadius: "8px",
              padding: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "16px" }}>✅</span>
              <div>
                <strong style={{ color: "#1565c0", fontSize: "14px" }}>
                  PIN 인증이 활성화되었습니다
                </strong>
                <div style={{ fontSize: "12px", color: "#1976d2" }}>
                  현재 PIN: {localPinSettings.code.replace(/./g, "●")}
                </div>
              </div>
            </div>
            <button
              onClick={handleEditPin}
              style={{
                background: "linear-gradient(45deg, #74b9ff, #0984e3)",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseOut={(e) => {
                e.target.style.transform = "translateY(0)";
              }}
            >
              PIN 변경
            </button>
          </div>
        ) : localPinSettings.enabled && !localPinSettings.code ? (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              padding: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "16px" }}>⚠️</span>
              <div>
                <strong style={{ color: "#856404", fontSize: "14px" }}>
                  PIN을 설정해주세요 (필수)
                </strong>
                <div style={{ fontSize: "12px", color: "#856404" }}>
                  현황판 접근을 위해 6자리 PIN이 필요합니다
                </div>
              </div>
            </div>
            <button
              onClick={handleSetPin}
              style={{
                background: "linear-gradient(45deg, #ffc107, #e0a800)",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseOut={(e) => {
                e.target.style.transform = "translateY(0)";
              }}
            >
              PIN 설정
            </button>
          </div>
        ) : (
          // 🎯 PIN이 비활성화된 경우 (실제로는 발생하지 않지만 기존 로직 유지)
          <div
            style={{
              background: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: "8px",
              padding: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "16px" }}>🔒</span>
            <div>
              <strong style={{ color: "#721c24", fontSize: "14px" }}>
                PIN 인증이 비활성화되었습니다
              </strong>
              <div style={{ fontSize: "12px", color: "#721c24" }}>
                보안을 위해 PIN 인증을 활성화해주세요
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PIN 코드 입력 모달 */}
      {showCodeInput && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCancelPin}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
                paddingBottom: "10px",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#2d3436",
                }}
              >
                🔐 PIN 코드 설정
              </h2>
              <button
                onClick={handleCancelPin}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#636e72",
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                  color: "#2d3436",
                }}
              >
                6자리 PIN 코드 (필수)
              </label>
              <input
                type="text"
                ref={inputRef} // 🎯 ref 추가
                value={tempCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setTempCode(value);
                }}
                placeholder="123456"
                maxLength="6"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    tempCode.length === 6 ? "#28a745" : "#ddd"
                  }`,
                  borderRadius: "8px",
                  fontSize: "18px",
                  textAlign: "center",
                  letterSpacing: "4px",
                  fontFamily: "monospace",
                  fontWeight: "bold",
                  transition: "border-color 0.2s",
                }}
                // 🎯 autoFocus 제거 (useEffect에서 안전하게 처리)
              />
              <small
                style={{
                  color: tempCode.length === 6 ? "#28a745" : "#666",
                  fontSize: "12px",
                  marginTop: "5px",
                  display: "block",
                  fontWeight: tempCode.length === 6 ? "500" : "normal",
                }}
              >
                {tempCode.length === 6
                  ? "✅ PIN 형식이 올바릅니다"
                  : "숫자 6자리를 입력하세요 (예: 123456)"}
              </small>
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCancelPin}
                style={{
                  background: "#636e72",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#495057";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#636e72";
                }}
              >
                취소
              </button>
              <button
                onClick={handleSavePin}
                disabled={tempCode.length !== 6}
                style={{
                  background:
                    tempCode.length === 6
                      ? "linear-gradient(45deg, #00b894, #00cec9)"
                      : "#ccc",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: tempCode.length === 6 ? "pointer" : "not-allowed",
                  transition: "all 0.2s",
                  opacity: tempCode.length === 6 ? 1 : 0.6,
                }}
                onMouseOver={(e) => {
                  if (tempCode.length === 6) {
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseOut={(e) => {
                  if (tempCode.length === 6) {
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 안내 정보 */}
      <div
        style={{
          background: "#e8f5e8",
          border: "1px solid #4caf50",
          borderRadius: "8px",
          padding: "15px",
          marginTop: "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <span style={{ fontSize: "16px" }}>ℹ️</span>
          <strong style={{ color: "#2e7d32" }}>PIN 인증 안내</strong>
        </div>
        <div
          style={{
            fontSize: "13px",
            color: "#2e7d32",
            lineHeight: 1.4,
            textAlign: "left",
          }}
        >
          <p style={{ margin: "4px 0" }}>
            • <strong>PIN 활성화:</strong> 현황판 접근 시 6자리 숫자 PIN 입력
            필요
          </p>
          <p style={{ margin: "4px 0" }}>
            • <strong>PIN 필수:</strong> 보안을 위해 PIN 비활성화는 불가능합니다
          </p>
          <p style={{ margin: "4px 0" }}>
            • <strong>숫자만 입력:</strong> 0-9 숫자 6자리 (예: 123456)
          </p>
          <p
            style={{
              margin: "8px 0 0 0",
              fontWeight: "bold",
              color: "#1b5e20",
              background: "#c8e6c9",
              padding: "6px 8px",
              borderRadius: "4px",
            }}
          >
            ⚠️ PIN 설정 후 반드시 "설정 저장" 버튼을 클릭하세요
          </p>
        </div>
      </div>
    </div>
  );
};

export default PinManagement;
